import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BookOpen,
  Clock,
  Play,
  Award,
  ChevronRight,
  GraduationCap,
  Target,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StudentDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [completedCourses, setCompletedCourses] = useState([]);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch enrollments
        const enrollRes = await fetch(`${BACKEND_URL}/api/enrollments/me`, {
          credentials: 'include'
        });
        
        let enrolledIds = [];
        if (enrollRes.ok) {
          const data = await enrollRes.json();
          
          const inProgress = data.filter(e => e.enrollment.progress < 100).map(e => ({
            ...e.course,
            progress: e.enrollment.progress,
            lastAccessed: formatTimeAgo(e.enrollment.last_accessed_at),
            enrollmentId: e.enrollment.id
          }));
          
          const completed = data.filter(e => e.enrollment.progress >= 100).map(e => ({
            ...e.course,
            progress: 100,
            completedAt: formatDate(e.enrollment.completed_at),
            enrollmentId: e.enrollment.id
          }));
          
          enrolledIds = data.map(e => e.course.id);
          setEnrolledCourses(inProgress);
          setCompletedCourses(completed);
        }

        // Fetch recommended courses (courses not enrolled in)
        const coursesRes = await fetch(`${BACKEND_URL}/api/courses?limit=10`);
        if (coursesRes.ok) {
          const coursesData = await coursesRes.json();
          const courses = coursesData.courses || coursesData;
          const notEnrolled = courses.filter(c => !enrolledIds.includes(c.id)).slice(0, 4);
          setRecommendedCourses(notEnrolled);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const totalCourses = enrolledCourses.length + completedCourses.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20" data-testid="student-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Avatar className="w-16 h-16 border-2 border-amber-500">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-amber-600 text-white text-xl">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                Welcome back, {user?.name?.split(' ')[0] || 'Learner'}!
              </h1>
              <p className="text-gray-400">Continue your learning journey</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <BookOpen className="w-5 h-5 text-amber-400" />
                </div>
                <p className="text-2xl font-bold text-white" data-testid="stat-total-courses">{totalCourses}</p>
                <p className="text-sm text-gray-500">Enrolled</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <GraduationCap className="w-5 h-5 text-emerald-400" />
                </div>
                <p className="text-2xl font-bold text-white" data-testid="stat-completed">{completedCourses.length}</p>
                <p className="text-sm text-gray-500">Completed</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Clock className="w-5 h-5 text-cyan-400" />
                </div>
                <p className="text-2xl font-bold text-white">{enrolledCourses.length}</p>
                <p className="text-sm text-gray-500">In Progress</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Award className="w-5 h-5 text-yellow-400" />
                </div>
                <p className="text-2xl font-bold text-white">{completedCourses.length}</p>
                <p className="text-sm text-gray-500">Certificates</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Courses Section */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="in-progress" className="w-full">
              <TabsList className="bg-gray-900 border border-gray-800 p-1">
                <TabsTrigger value="in-progress" className="data-[state=active]:bg-gray-800" data-testid="tab-in-progress">
                  In Progress ({enrolledCourses.length})
                </TabsTrigger>
                <TabsTrigger value="completed" className="data-[state=active]:bg-gray-800" data-testid="tab-completed">
                  Completed ({completedCourses.length})
                </TabsTrigger>
              </TabsList>

              {/* In Progress Courses */}
              <TabsContent value="in-progress" className="mt-6">
                <div className="space-y-4">
                  {enrolledCourses.map((course) => (
                    <Card
                      key={course.id}
                      className="bg-gray-900/50 border-gray-800 hover:border-gray-700 transition-all cursor-pointer"
                      onClick={() => navigate(`/learn/${course.slug}`)}
                      data-testid={`enrolled-course-${course.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          <div className="w-40 h-24 rounded-lg overflow-hidden shrink-0">
                            <img
                              src={course.thumbnail}
                              alt={course.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <Badge className="bg-gray-800 text-gray-300 mb-2">
                                  {course.category}
                                </Badge>
                                <h3 className="text-lg font-semibold text-white mb-1 line-clamp-1">
                                  {course.title}
                                </h3>
                                <p className="text-sm text-gray-500 mb-3">
                                  Last accessed {course.lastAccessed}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex-1 mr-4">
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span className="text-gray-400">{Math.round(course.progress)}% complete</span>
                                </div>
                                <Progress value={course.progress} className="h-2" />
                              </div>
                              <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black">
                                <Play className="w-4 h-4 mr-1" />
                                Continue
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {enrolledCourses.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                        <BookOpen className="w-8 h-8 text-gray-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2">No courses in progress</h3>
                      <p className="text-gray-400 mb-6">Start your learning journey today!</p>
                      <Button
                        onClick={() => navigate('/courses')}
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                        data-testid="browse-courses-btn"
                      >
                        Browse Courses
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Completed Courses */}
              <TabsContent value="completed" className="mt-6">
                <div className="space-y-4">
                  {completedCourses.map((course) => (
                    <Card
                      key={course.id}
                      className="bg-gray-900/50 border-gray-800 hover:border-gray-700 transition-all"
                    >
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          <div className="w-40 h-24 rounded-lg overflow-hidden shrink-0 relative">
                            <img
                              src={course.thumbnail}
                              alt={course.title}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-emerald-500/20 flex items-center justify-center">
                              <Award className="w-8 h-8 text-emerald-400" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 mb-2">
                              Completed
                            </Badge>
                            <h3 className="text-lg font-semibold text-white mb-1">
                              {course.title}
                            </h3>
                            <p className="text-sm text-gray-500 mb-3">
                              Completed on {course.completedAt}
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-gray-700 text-white hover:bg-gray-800"
                              onClick={() => navigate(`/course/${course.slug}`)}
                            >
                              Review Course
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {completedCourses.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                        <GraduationCap className="w-8 h-8 text-gray-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2">No completed courses</h3>
                      <p className="text-gray-400">Keep learning to earn your first certificate!</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Recommended Courses */}
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Recommended</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/courses')}
                    className="text-amber-400 hover:text-amber-300"
                  >
                    View all
                  </Button>
                </div>
                <div className="space-y-4">
                  {recommendedCourses.map((course) => (
                    <div
                      key={course.id}
                      className="flex gap-3 cursor-pointer group"
                      onClick={() => navigate(`/course/${course.slug}`)}
                      data-testid={`recommended-course-${course.id}`}
                    >
                      <div className="w-20 h-14 rounded-lg overflow-hidden shrink-0">
                        <img
                          src={course.thumbnail}
                          alt={course.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium truncate group-hover:text-amber-400 transition-colors">
                          {course.title}
                        </p>
                        <p className="text-xs text-amber-400 font-medium">
                          {course.pricing_type === 'free' ? 'Free' : `$${course.price}`}
                        </p>
                      </div>
                    </div>
                  ))}
                  {recommendedCourses.length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-4">Explore our course catalog!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
